import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import { submitServiceBooking } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const allServices = [
  'Wheel Alignment',
  'Wheel Balancing',
  'Tire Installation',
  'Tire Rotation',
  'Nitrogen Filling',
  'Tire Inspection'
];

const timeSlots = [
  '9:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '1:00 PM', '2:00 PM',
  '3:00 PM', '4:00 PM', '5:00 PM'
];

const ServiceBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const preSelectedService = location.state?.service || '';

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    vehicleBrand: '',
    vehicleModel: '',
    vehicleYear: '',
    service: preSelectedService,
    additionalServices: [],
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdditionalService = (service) => {
    if (service === formData.service) return;
    const current = formData.additionalServices;
    if (current.includes(service)) {
      setFormData({ ...formData, additionalServices: current.filter(s => s !== service) });
    } else {
      setFormData({ ...formData, additionalServices: [...current, service] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service) return setError('Please select a service.');
    setError('');
    setLoading(true);
    try {
      await submitServiceBooking({
        // User info from auth profile
        customerName: user?.name || 'Guest',
        phone: user?.phone || '',
        email: user?.email || '',
        // Vehicle + service details from form
        ...formData
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <Navbar />
        <div style={styles.successPage}>
          <div style={styles.successCard}>
            <span style={styles.successIcon}>🔧</span>
            <h2 style={styles.successTitle}>Booking Request Sent!</h2>
            <p style={styles.successText}>
              Your service booking for <strong>{formData.service}</strong> has been received.
              {user?.phone && <> We will contact you on <strong>{user.phone}</strong> to confirm.</>}
            </p>
            <p style={{ color: '#4a5568', fontSize: '13px', marginBottom: '24px' }}>
              Track your booking under <strong>My Account → My Services</strong>.
            </p>
            <div style={styles.successBtns}>
              <button style={styles.primaryBtn} onClick={() => navigate('/my-account')}>My Account →</button>
              <button style={styles.secondaryBtn} onClick={() => navigate('/')}>Back to Home</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: '#f5f7fa', minHeight: '100vh' }}>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Book a Service</h1>
          <p style={styles.subtitle}>Select your services and vehicle details — we'll confirm your appointment</p>
        </div>

        {/* User info banner — if logged in */}
        {user && (
          <div style={styles.userBanner}>
            <span style={{ fontSize: '20px' }}>👤</span>
            <div>
              <p style={{ margin: 0, fontWeight: '600', color: '#1a3a5c', fontSize: '14px' }}>{user.name}</p>
              <p style={{ margin: 0, color: '#4a5568', fontSize: '12px' }}>{user.email}{user.phone ? ` · ${user.phone}` : ''}</p>
            </div>
            <span style={styles.userBannerNote}>Your details will be sent automatically</span>
          </div>
        )}

        {!user && (
          <div style={styles.loginPrompt}>
            💡 <strong>Tip:</strong> <button style={styles.loginLink} onClick={() => navigate('/login')}>Log in</button> to auto-fill your contact details and track this booking in My Account.
          </div>
        )}

        <div style={styles.grid}>
          {/* Left — Service Selection */}
          <div>
            <h2 style={styles.sectionTitle}>Select Service</h2>
            <div style={styles.servicesList}>
              {allServices.map((service, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.serviceOption,
                    borderColor: formData.service === service ? '#c41e3a' : '#e2e8f0',
                    background: formData.service === service ? '#fff5f5' : '#fff'
                  }}
                  onClick={() => setFormData({ ...formData, service })}
                >
                  <div style={styles.serviceOptionLeft}>
                    <div style={{
                      ...styles.radioCircle,
                      borderColor: formData.service === service ? '#c41e3a' : '#ddd',
                      background: formData.service === service ? '#c41e3a' : 'transparent'
                    }} />
                    <span style={styles.serviceName}>{service}</span>
                  </div>
                </div>
              ))}
            </div>

            <h2 style={{ ...styles.sectionTitle, marginTop: '24px' }}>Add More Services</h2>
            <div style={styles.additionalServices}>
              {allServices.filter(s => s !== formData.service).map((service, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.additionalChip,
                    background: formData.additionalServices.includes(service) ? '#c41e3a' : '#fff',
                    color: formData.additionalServices.includes(service) ? '#fff' : '#444',
                    borderColor: formData.additionalServices.includes(service) ? '#c41e3a' : '#e2e8f0'
                  }}
                  onClick={() => handleAdditionalService(service)}
                >
                  {service}
                </div>
              ))}
            </div>
          </div>

          {/* Right — Vehicle + Schedule */}
          <div>
            <h2 style={styles.sectionTitle}>Vehicle & Schedule</h2>
            <div style={styles.formCard}>
              {error && <div style={styles.errorBox}>⚠️ {error}</div>}

              <form onSubmit={handleSubmit}>
                <div style={styles.formRow}>
                  <div style={styles.field}>
                    <label style={styles.label}>Vehicle Brand *</label>
                    <input style={styles.input} type="text" name="vehicleBrand" placeholder="e.g. Toyota" value={formData.vehicleBrand} onChange={handleChange} required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Vehicle Model *</label>
                    <input style={styles.input} type="text" name="vehicleModel" placeholder="e.g. Corolla" value={formData.vehicleModel} onChange={handleChange} required />
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Vehicle Year</label>
                  <input style={styles.input} type="text" name="vehicleYear" placeholder="e.g. 2020" value={formData.vehicleYear} onChange={handleChange} />
                </div>

                <div style={styles.formRow}>
                  <div style={styles.field}>
                    <label style={styles.label}>Preferred Date</label>
                    <input style={styles.input} type="date" name="preferredDate" value={formData.preferredDate} onChange={handleChange} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Preferred Time</label>
                    <select style={styles.input} name="preferredTime" value={formData.preferredTime} onChange={handleChange}>
                      <option value="">Select time</option>
                      {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Additional Notes</label>
                  <textarea style={{ ...styles.input, height: '80px', resize: 'vertical' }} name="message" placeholder="Any additional information..." value={formData.message} onChange={handleChange} />
                </div>

                {formData.service && (
                  <div style={styles.bookingSummary}>
                    <p style={styles.summaryTitle}>📋 Booking Summary</p>
                    <p style={styles.summaryItem}>✅ Main: <strong>{formData.service}</strong></p>
                    {formData.additionalServices.length > 0 && (
                      <p style={styles.summaryItem}>➕ Additional: <strong>{formData.additionalServices.join(', ')}</strong></p>
                    )}
                    {formData.vehicleBrand && <p style={styles.summaryItem}>🚗 Vehicle: <strong>{formData.vehicleBrand} {formData.vehicleModel} {formData.vehicleYear}</strong></p>}
                    {formData.preferredDate && <p style={styles.summaryItem}>📅 Date: <strong>{formData.preferredDate}</strong>{formData.preferredTime && <> at <strong>{formData.preferredTime}</strong></>}</p>}
                  </div>
                )}

                <button style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }} type="submit" disabled={loading || !formData.service}>
                  {loading ? 'Sending...' : '🔧 Confirm Booking Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container: { maxWidth: '1100px', margin: '0 auto', padding: '40px 20px' },
  header: { textAlign: 'center', marginBottom: '28px' },
  title: { color: '#1a3a5c', fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' },
  subtitle: { color: '#4a5568', fontSize: '15px' },

  userBanner: { display: 'flex', alignItems: 'center', gap: '14px', background: '#f0f7f0', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '14px 20px', marginBottom: '24px' },
  userBannerNote: { marginLeft: 'auto', color: '#15803d', fontSize: '12px', fontWeight: '600' },
  loginPrompt: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '12px 18px', marginBottom: '24px', fontSize: '14px', color: '#856404' },
  loginLink: { background: 'none', border: 'none', color: '#c41e3a', fontWeight: '700', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline', padding: 0 },

  grid: { display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: '32px' },
  sectionTitle: { color: '#1a3a5c', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' },
  servicesList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  serviceOption: { display: 'flex', alignItems: 'center', padding: '13px 18px', borderRadius: '12px', border: '2px solid', cursor: 'pointer', transition: 'all 0.2s' },
  serviceOptionLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  radioCircle: { width: '18px', height: '18px', borderRadius: '50%', border: '2px solid', transition: 'all 0.2s', flexShrink: 0 },
  serviceName: { color: '#1a3a5c', fontSize: '14px', fontWeight: '500' },
  additionalServices: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
  additionalChip: { padding: '8px 16px', borderRadius: '20px', border: '1px solid', cursor: 'pointer', fontSize: '13px', fontWeight: '500', transition: 'all 0.2s' },

  formCard: { background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  errorBox: { background: '#fff5f5', color: '#c41e3a', border: '1px solid #ffd0d3', borderRadius: '10px', padding: '12px 16px', fontSize: '14px', marginBottom: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '14px' },
  label: { display: 'block', color: '#4a5568', fontSize: '13px', fontWeight: '600', marginBottom: '6px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '9px', border: '1px solid #e2e8f0', background: '#f9fafc', color: '#1a3a5c', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  bookingSummary: { background: '#f5f7fa', borderRadius: '10px', padding: '14px', marginBottom: '14px', border: '1px solid #e2e8f0' },
  summaryTitle: { color: '#1a3a5c', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' },
  summaryItem: { color: '#4a5568', fontSize: '13px', marginBottom: '4px' },
  submitBtn: { width: '100%', padding: '14px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 16px rgba(196,30,58,0.25)' },

  successPage: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: '40px 20px' },
  successCard: { background: '#fff', borderRadius: '20px', padding: '48px 40px', textAlign: 'center', maxWidth: '480px', boxShadow: '0 8px 40px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' },
  successIcon: { fontSize: '64px', display: 'block', marginBottom: '20px' },
  successTitle: { color: '#1a3a5c', fontSize: '26px', fontWeight: 'bold', marginBottom: '12px' },
  successText: { color: '#4a5568', fontSize: '15px', lineHeight: 1.7, marginBottom: '12px' },
  successBtns: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn: { padding: '12px 28px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' },
  secondaryBtn: { padding: '12px 28px', background: '#f5f7fa', color: '#444', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px' }
};

export default ServiceBookingPage;